import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@fluentui/react-components";
import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from "@fluentui/react-components";

// Components
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useSelectedCiRelationTypesContext } from "../context/SelectedCiRelationTypesContext";
import { useCiRelationTypesQuery } from "../hooks/useCiRelationTypeQueries";

const useStyles = makeStyles({
  container: {
    height: "calc(100dvh - 200px)",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

const CiRelationTypesDataGrid = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { value: selectedItems, setValue: setSelectedItems } = useSelectedCiRelationTypesContext();

  // Errors
  const [errors, setErrors] = useState([]);

  const { data = [], isFetching, error, refetch } = useCiRelationTypesQuery();

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, t]);

  if (isFetching) return <Spinner label={t("generic.loading")} />;

  const columns = [
    createTableColumn({
      columnId: "code",
      renderHeaderCell: () => t("ciRelationType.code", { defaultValue: "Code" }),
      renderCell: (item) => <TableCellLayout>{item.code}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "name",
      renderHeaderCell: () => t("ciRelationType.name", { defaultValue: "Name" }),
      renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "reverse_name",
      renderHeaderCell: () => t("ciRelationType.reverse_name", { defaultValue: "Reverse name" }),
      renderCell: (item) => <TableCellLayout>{item.reverse_name}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "bidirectional",
      renderHeaderCell: () => t("ciRelationType.bidirectional", { defaultValue: "Bidirectional" }),
      renderCell: (item) => (
        <TableCellLayout>
          {item.bidirectional ? t("generic.yes", { defaultValue: "Yes" }) : t("generic.no", { defaultValue: "No" })}
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "description",
      renderHeaderCell: () => t("generic.description"),
      renderCell: (item) => <TableCellLayout>{item.description}</TableCellLayout>,
    }),
  ];

  const columnSizingOptions = {
    code: { minWidth: 60, defaultWidth: 80, idealWidth: 100 },
    description: { idealWidth: 300 },
  };

  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const newSelection = data.filter((l) => ids.includes(l.id));
    setSelectedItems(newSelection);
  };

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <DataGrid
        items={data}
        columns={columns}
        columnSizingOptions={columnSizingOptions}
        selectedItems={selectedItems.map((item) => item.id)}
        getRowId={(item) => item.id}
        focusMode="composite"
        resizableColumns
        selectionMode="single"
        onSelectionChange={handleSelectionChange}
      >
        <DataGridHeader>
          <DataGridRow
            className={styles.stickyHeaderRow}
            selectionCell={{ checkboxIndicator: { "aria-label": "Select all rows" } }}
          >
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>

        <div className={styles.scrollArea}>
          <DataGridBody>
            {({ item, rowId }) => (
              <DataGridRow key={rowId} selectionCell={{ checkboxIndicator: { "aria-label": "Select row" } }}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </div>
      </DataGrid>
    </div>
  );
};

export default CiRelationTypesDataGrid;
